import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { formatMinor } from '../../lib/money';
import { useCurrency, type Message, type Person, type Route, type Stop } from './shared';

interface Seat {
  id: string;
  studentName: string;
  admissionNumber: string;
  routeName: string;
  stopName: string;
  monthlyFeeMinor: number;
  startDate: string;
}

export function SeatsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const currency = useCurrency(schoolId);
  const assigning = can('transport.assign', schoolId);
  const [routeId, setRouteId] = useState('');
  const [stopId, setStopId] = useState('');
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<Person | null>(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<Message>(null);

  const routes = useQuery({
    queryKey: ['transport', schoolId, 'routes'],
    queryFn: () =>
      apiList<Route>(`/schools/${schoolId}/transport/routes`, { query: { pageSize: 100 } }),
  });
  const detail = useQuery({
    queryKey: ['transport', schoolId, 'route', routeId],
    queryFn: () => api<{ stops: Stop[] }>(`/schools/${schoolId}/transport/routes/${routeId}`),
    enabled: Boolean(routeId),
  });
  const matches = useQuery({
    queryKey: ['transport', schoolId, 'students', search],
    queryFn: () =>
      apiList<Person>('/students', { query: { schoolId, q: search, pageSize: 6 } }).then(
        (r) => r.data,
      ),
    enabled: assigning && search.trim().length >= 2 && !person,
  });
  const seats = useQuery({
    queryKey: ['transport', schoolId, 'seats', filter, page],
    queryFn: () =>
      apiList<Seat>(`/schools/${schoolId}/transport/assignments`, {
        query: { status: 'active', routeId: filter || undefined, page, pageSize: 20 },
      }),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['transport', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const assign = useMutation({
    mutationFn: () =>
      api(`/schools/${schoolId}/transport/assignments`, {
        method: 'POST',
        body: { studentId: person!.id, routeId, stopId },
      }),
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('tr.seated') });
      setPerson(null);
      setSearch('');
      refresh();
    },
    onError: fail,
  });
  const end = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/transport/assignments/${id}/end`, { method: 'POST', body: {} }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: fail,
  });

  const stops = detail.data?.stops ?? [];
  const chosenStop = stopId || stops[0]?.id || '';

  return (
    <section aria-label={t('tr.tab.seats')} className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {assigning && (
        <form
          noValidate
          aria-label={t('tr.giveSeat')}
          className="grid items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (person && routeId && chosenStop) {
              setStopId(chosenStop);
              assign.mutate();
            }
          }}
        >
          <div className="relative">
            <TextField
              label={t('tr.findStudent')}
              value={person ? `${person.firstName} ${person.lastName}` : search}
              onChange={(e) => {
                setPerson(null);
                setSearch(e.target.value);
              }}
            />
            {!person && matches.data && matches.data.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full divide-y divide-slate-100 rounded border border-slate-200 bg-white text-sm shadow">
                {matches.data.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="w-full p-2 text-left hover:bg-slate-50"
                      onClick={() => setPerson(p)}
                    >
                      {p.firstName} {p.lastName} · {p.admissionNumber}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label htmlFor="seat-route" className="block text-sm font-medium">
              {t('tr.route')}
            </label>
            <select
              id="seat-route"
              className={selectClass}
              value={routeId}
              onChange={(e) => {
                setRouteId(e.target.value);
                setStopId('');
              }}
            >
              <option value="">{t('tr.choose')}</option>
              {routes.data?.data
                .filter((r) => r.status === 'active')
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({t('tr.seatsTaken', { taken: r.seated, total: r.capacity ?? 0 })})
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label htmlFor="seat-stop" className="block text-sm font-medium">
              {t('tr.stop')}
            </label>
            <select
              id="seat-stop"
              className={selectClass}
              value={chosenStop}
              disabled={!routeId}
              onChange={(e) => setStopId(e.target.value)}
            >
              {stops.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {formatMinor(s.monthlyFeeMinor, currency)}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className={primaryButton}
            disabled={!person || !routeId || !chosenStop || assign.isPending}
          >
            {t('tr.giveSeat')}
          </button>
        </form>
      )}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{t('tr.currentSeats')}</h2>
          <div>
            <label htmlFor="seat-filter" className="mr-2 text-sm">
              {t('tr.filterRoute')}
            </label>
            <select
              id="seat-filter"
              className={selectClass}
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('tr.allRoutes')}</option>
              {routes.data?.data.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {seats.data?.data.length === 0 && <p className="text-slate-600">{t('tr.noSeats')}</p>}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {seats.data?.data.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <span>
                <span className="font-medium">{s.studentName}</span> · {s.admissionNumber} ·{' '}
                {s.routeName} → {s.stopName} ·{' '}
                {t('tr.perMonth', { amount: formatMinor(s.monthlyFeeMinor, currency) })}
              </span>
              {assigning && (
                <button
                  type="button"
                  className={secondaryButton}
                  aria-label={`${t('tr.endSeat')}: ${s.studentName}`}
                  disabled={end.isPending}
                  onClick={() =>
                    window.confirm(t('tr.confirmEnd', { name: s.studentName })) && end.mutate(s.id)
                  }
                >
                  {t('tr.endSeat')}
                </button>
              )}
            </li>
          ))}
        </ul>
        {seats.data && seats.data.meta.totalPages > 1 && (
          <Pager page={page} totalPages={seats.data.meta.totalPages} onPage={setPage} />
        )}
      </div>
    </section>
  );
}
